import React, { useMemo } from "react";
import type { FC } from "react";

import cn from "classnames";

import { calcQuantity } from "./utils";

import type { CounterLowSize, CounterSize, CounterViews } from "./types";
import type { Never, Optional, PropsWithBaseNever } from "../../types/react";

import classes from "./Counter.styl";

const COUNTER_DISPLAY_NAME = "Counter";
export const COUNTER_TEST_ID = "counter-test-id";
export const DEFAULT_MAX_QUANTITY = 100;
export const DEFAULT_QUANTITY = 0;
const MIN_SIZE_WITH_TEXT = 16;

export interface ICounterBaseProps {
    /** Отвечает за наличие обводки у основного фрейма. Толщина линии обводки предопределена параметром size */
    stroke?: boolean;
    /** Принимает селектор, в котором можно переопределить цвет обводки (--counter-stroke-color), если вам надо заменить другие переменные смотрите "Counter With Custom Colors" */
    customStrokeColor?: string;
}

export interface ICounterLowSizeBaseProps {
    /** Отвечает за исходный размер индикатора и значения, которые идут в комбинации с этим размером */
    size?: CounterLowSize;
    /** Отвечает за добавление анимации пульсации */
    pulse?: boolean;
}

export interface ICounterRegularSizeBaseProps {
    size: Exclude<CounterSize, CounterLowSize>;
    /** Отвечает за значение в счетчике количества */
    quantity: number;
    /** Отвечает за максимальное значение счетчика, достигая которого отображается предыдущее число со знаком + */
    maxQuantity?: number;
}

type WithSizes<T = unknown> = T &
    (
        | (ICounterRegularSizeBaseProps &
              Never<ICounterLowSizeBaseProps, "pulse">)
        | (ICounterLowSizeBaseProps &
              Never<ICounterRegularSizeBaseProps, "quantity" | "maxQuantity">)
    );

export type CounterPropsRegularSize = ICounterRegularSizeBaseProps;

export type CounterProps = Optional<
    PropsWithBaseNever<WithSizes<ICounterBaseProps>, CounterViews>,
    "data-tsid"
>;

/**
 * Базовый компонент, фокусирующий на себе внимание пользователя и информирующий о количестве информации.
 * Может отображать строчку число-буквенных значений до 3 символов. В маленьком размере и без передачи количества представляет из себя обычный круг.
 *
 * <a href="https://www.figma.com/design/vTg3KAvmAXA9LigeyiqCWL/%F0%9F%92%A0-Base-Components?m=auto&node-id=2-22611&t=xOS5YJTI37Iwp4AM-1" target="_blank">Спека</a>
 * @constructor
 *
 * @deprecated Используйте компоненты Indicator или IndicatorCounter.
 */
export const Counter: FC<CounterProps> = (props): JSX.Element => {
    const {
        view = "primary",
        size = "8",
        stroke = false,
        customStrokeColor,
        "data-tsid": tsid,
        customColorsStyles,
        pulse,
        quantity = DEFAULT_QUANTITY,
        maxQuantity = DEFAULT_MAX_QUANTITY,
    } = props;

    const currentQuantity = useMemo(
        () =>
            Number(size) >= MIN_SIZE_WITH_TEXT
                ? calcQuantity(quantity, maxQuantity)
                : "",
        [size, quantity, maxQuantity],
    );

    const wrapperClasses = cn(
        classes["wrapper"],
        {
            [classes["__pulse"]]: pulse,
            [classes["__stroke"]]: stroke,
            [classes[`__size-${size}`]]: size,
            [classes[`__style-${view}`]]: view,
        },
        customColorsStyles,
        customStrokeColor,
    );

    return (
        <div
            className={wrapperClasses}
            data-tsid={tsid || COUNTER_TEST_ID}
            data-uikit={COUNTER_DISPLAY_NAME}
        >
            <div className={classes["counter"]}>{currentQuantity}</div>
        </div>
    );
};

Counter.displayName = COUNTER_DISPLAY_NAME;

export default Counter;
